import React, { Component, useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import Briefing from './pages/Briefing';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import AuthMagicLink from './pages/AuthMagicLink';
import Dashboard from './pages/Dashboard';
import Skills from './pages/Skills';
import Admin from './pages/Admin';
import TenantForm from './pages/TenantForm';
import Statistics from './pages/Statistics';
import Tickets from "./pages/Tickets";
import Emails from './pages/Emails';
import DeveloperChatLite from './pages/DeveloperChatLite';
// Chat + Meetings: entfernt 2026-04-13 (Rollout 2026-04-14)
// MailTriage ist jetzt als Tab in Emails.js integriert
import ObjektReport from './pages/ObjektReport';
import IW24Knowledge from './pages/IW24Knowledge';
import Chat from './pages/Chat';
import PostfachUebergabe from './pages/PostfachUebergabe';
import FeedbackWidget from './components/FeedbackWidget';
import Icon from './components/Icon';
import SidebarBrand from './components/brand/SidebarBrand';
import './styles/platform-design-system.css';
import './styles/responsive.css';

// ── Auth Context ──
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const API_URL = process.env.REACT_APP_API_URL || '/api';
const APP_TITLE = 'One. immo Plattform';
const ROLE_LABELS = { admin: 'Admin', manager: 'Teamleitung', entwickler: 'Entwickler', mitarbeiter: 'Mitarbeiter', readonly: 'Readonly' };

function getNavItems(user) {
  const isAdmin = user?.role === 'admin';
  const disabled = user?.disabled_features || [];
  return [
    { path: '/', label: 'Dashboard', icon: 'dashboard', locked: !isAdmin },
    { path: '/emails', label: 'E-Mails', icon: 'mail', earlyDev: true },
    { path: '/skills', label: 'Skills', icon: 'skills' },
    { path: '/iw24-knowledge', label: 'IW24-Assistent', icon: 'chat', earlyDev: true },
    { path: '/briefing', label: 'Briefing', icon: 'report', earlyDev: true },
    { path: '/operator', label: 'Operator', icon: 'chat' },
    !disabled.includes('developer-chat-lite') && { path: '/developer-chat', label: 'Dev-Chat', icon: 'chat', earlyDev: true },
    !disabled.includes('statistics') && { path: '/statistics', label: 'Statistiken', icon: 'chart' },
    { path: '/tickets', label: 'Tickets', icon: 'ticket', earlyDev: true },
    { path: '/objekt-report', label: 'Objekt-Report', icon: 'report' },
    isAdmin && { path: '/admin', label: 'Verwaltung', icon: 'users' },
    isAdmin && { path: '/postfach-uebergabe', label: 'Postfach-Übergabe', icon: 'handover' },
  ].filter(Boolean);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('fleet_token'));

  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => { setUser(data); setLoading(false); })
        .catch(() => { localStorage.removeItem('fleet_token'); setToken(null); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (newToken) => {
    localStorage.setItem('fleet_token', newToken);
    setLoading(true);   // Verhindert Redirect zu /login bevor /auth/me antwortet
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('fleet_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Global Job Context (Multi-Job: mehrere Skills parallel) ──
const JobContext = createContext(null);

export function useJobTracker() {
  return useContext(JobContext);
}

const JOB_LABELS = { 'mietvertrag': 'Mietvertragsprüfung', 'op-liste': 'OP-Liste', 'begehung-tickets': 'Begehungs-Tickets', 'sepa': 'Kontaktdaten & Datenschutz', 'mieteranlage': 'Mieteranlage', 'mahnwesen': 'Mahnwesen', 'eigentuemer-report': 'Eigentümer-Report', 'schwarzes-brett': 'Schwarzes Brett', 'kontobestaende': 'Kontobestände', 'ustva': 'USt-Voranmeldung', 'etv-tickets': 'ETV-Tickets', 'staffelmiete': 'Staffelmiete', 'kontenjournal': 'Kontenjournal' };
const JOB_INIT_MSGS = { 'mietvertrag': 'Dokumente werden verarbeitet...', 'op-liste': 'Login bei Immoware...', 'begehung-tickets': 'Protokoll wird analysiert...', 'sepa': 'Dokumente werden analysiert...', 'mieteranlage': 'Mietvertragsdokumente werden analysiert...', 'mahnwesen': 'Login bei Immoware...', 'eigentuemer-report': 'PDFs werden analysiert...', 'schwarzes-brett': 'E-Mail wird analysiert...', 'ustva': 'Login bei Immoware...', 'etv-tickets': 'ETV-Protokoll wird analysiert...', 'staffelmiete': 'Mieter werden geladen...', 'kontenjournal': 'Login bei Immoware...' };
const MAX_POLL_ERRORS = 15;

function JobProvider({ children }) {
  const { token } = useAuth();
  // jobs: Map<jobId, JobData> — mehrere Jobs gleichzeitig
  const [jobs, setJobs] = useState({});
  const pollRefs = useRef({}); // { jobId: intervalId }
  const errorCounts = useRef({}); // { jobId: count }

  const stopPolling = useCallback((jobId) => {
    if (jobId) {
      if (pollRefs.current[jobId]) {
        clearInterval(pollRefs.current[jobId]);
        delete pollRefs.current[jobId];
      }
      delete errorCounts.current[jobId];
    } else {
      // Alle stoppen
      Object.values(pollRefs.current).forEach(id => clearInterval(id));
      pollRefs.current = {};
      errorCounts.current = {};
    }
  }, []);

  const startTracking = useCallback((jobId, skillType = 'op-liste') => {
    // Falls dieser Job schon läuft, nicht doppelt starten
    if (pollRefs.current[jobId]) return;
    errorCounts.current[jobId] = 0;

    const label = JOB_LABELS[skillType] || skillType;
    const initMsg = JOB_INIT_MSGS[skillType] || 'Wird verarbeitet...';

    setJobs(prev => ({ ...prev, [jobId]: { jobId, skillType, progress: 0, message: initMsg, phase: 'upload', status: 'running', result: null } }));

    const endpoint = `${API_URL}/skills/${skillType}/progress/${jobId}`;

    pollRefs.current[jobId] = setInterval(async () => {
      try {
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          errorCounts.current[jobId] = (errorCounts.current[jobId] || 0) + 1;
          if (errorCounts.current[jobId] >= MAX_POLL_ERRORS) {
            stopPolling(jobId);
            setJobs(prev => prev[jobId] ? { ...prev, [jobId]: { ...prev[jobId], status: 'error', message: 'Verbindung zum Server verloren.' } } : prev);
          }
          return;
        }
        errorCounts.current[jobId] = 0;
        const data = await res.json();

        // Title: zeige Fortschritt des ältesten laufenden Jobs
        const runningCount = Object.values(pollRefs.current).length;
        if (data.status === 'done' || data.status === 'error') {
          document.title = runningCount <= 1 ? APP_TITLE : `(${runningCount - 1} Jobs) ${APP_TITLE}`;
        } else {
          document.title = runningCount > 1
            ? `(${runningCount} Jobs) ${APP_TITLE}`
            : `(${data.progress}%) ${label} - ${APP_TITLE}`;
        }

        setJobs(prev => prev[jobId] ? { ...prev, [jobId]: {
          ...prev[jobId],
          progress: data.progress,
          message: data.message,
          phase: data.phase,
          status: data.status,
          result: data.result,
          partial_result: data.partial_result,
        }} : prev);

        if (data.status === 'done' || data.status === 'error') {
          stopPolling(jobId);
          if (Object.keys(pollRefs.current).length === 0) {
            document.title = APP_TITLE;
          }
        }
      } catch (err) {
        errorCounts.current[jobId] = (errorCounts.current[jobId] || 0) + 1;
        if (errorCounts.current[jobId] >= MAX_POLL_ERRORS) {
          stopPolling(jobId);
          setJobs(prev => prev[jobId] ? { ...prev, [jobId]: { ...prev[jobId], status: 'error', message: 'Verbindung zum Server verloren.' } } : prev);
        }
      }
    }, 2000);
  }, [token, stopPolling]);

  const dismiss = useCallback((jobId) => {
    if (jobId) {
      stopPolling(jobId);
      setJobs(prev => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    } else {
      stopPolling();
      setJobs({});
    }
    if (Object.keys(pollRefs.current).length === 0) {
      document.title = APP_TITLE;
    }
  }, [stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Abwärtskompatibilität: job = erster laufender oder letzter fertige Job
  const jobList = Object.values(jobs);
  const job = jobList.find(j => j.status === 'running') || jobList[jobList.length - 1] || null;

  return (
    <JobContext.Provider value={{ job, jobs, startTracking, dismiss }}>
      {children}
    </JobContext.Provider>
  );
}

// ── Einzelnes Job-Fenster ──
function JobNotificationCard({ job, index, onDismiss, onMinimize, minimized }) {
  const { token } = useAuth();
  const isDone = job.status === 'done';
  const isError = job.status === 'error';
  const isRunning = job.status === 'running';

  const statusLabels = {
    'mietvertrag': { done: 'Prüfbericht fertig', error: 'Prüfung fehlgeschlagen', running: 'Bewerbermappe wird geprüft...' },
    'op-liste': { done: 'OP-Liste fertig', error: 'OP-Liste Fehler', running: 'OP-Liste wird erstellt...' },
    'begehung-tickets': { done: 'Tickets erstellt', error: 'Ticket-Erstellung fehlgeschlagen', running: 'Begehung wird verarbeitet...' },
    'sepa': { done: 'Analyse abgeschlossen', error: 'Analyse fehlgeschlagen', running: 'Dokumente werden analysiert...' },
    'mieteranlage': { done: 'Mieteranlage fertig', error: 'Mieteranlage Fehler', running: 'Mieteranlage wird erstellt...' },
    'mahnwesen': { done: 'Mahnlauf abgeschlossen', error: 'Mahnlauf Fehler', running: 'Mahnlauf wird durchgeführt...' },
    'eigentuemer-report': { done: 'Report fertig', error: 'Report Fehler', running: 'Report wird erstellt...' },
    'schwarzes-brett': { done: 'Aushang erstellt', error: 'Aushang Fehler', running: 'Aushang wird erstellt...' },
    'kontobestaende': { done: 'Kontobestände fertig', error: 'Kontobestände Fehler', running: 'Kontobestände werden abgerufen...' },
    'etv-tickets': { done: 'ETV-Tickets fertig', error: 'ETV-Analyse Fehler', running: 'ETV-Protokoll wird analysiert...' },
  };
  const l = statusLabels[job.skillType] || { done: 'Fertig', error: 'Fehler', running: 'Wird verarbeitet...' };
  const headerText = isDone ? l.done : isError ? l.error : l.running;

  if (minimized && isRunning) {
    return (
      <div
        onClick={() => onMinimize(job.jobId, false)}
        style={{
          position: 'fixed', top: 64 + index * 56, right: 16, zIndex: 9999,
          width: 48, height: 48, borderRadius: 12,
          background: 'var(--brand-navy)', color: 'var(--brand-gold)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          boxShadow: 'var(--shadow-lg)',
          fontFamily: 'var(--font-body)',
          transition: 'top 0.3s ease',
        }}
        title={JOB_LABELS[job.skillType] || job.skillType}
      >
        {job.progress}%
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', top: 64 + index * 108, right: 16, zIndex: 9999 - index,
      width: 320, borderRadius: 8, overflow: 'hidden',
      background: '#fff', border: `1px solid ${isDone ? 'var(--brand-success)' : isError ? 'var(--brand-danger)' : 'var(--brand-warm-gray)'}`,
      boxShadow: 'var(--shadow-lg)',
      fontFamily: 'var(--font-body)',
      transition: 'top 0.3s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: isDone ? 'var(--brand-success)' : isError ? 'var(--brand-danger)' : 'var(--brand-navy)',
        color: '#fff',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {headerText}
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 8, flexShrink: 0 }}>
          {isRunning && (
            <button onClick={() => onMinimize(job.jobId, true)} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              width: 22, height: 22, borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1,
            }}>−</button>
          )}
          {(isDone || isError) && (
            <button onClick={() => onDismiss(job.jobId)} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              width: 22, height: 22, borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1,
            }}>×</button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        {isRunning && (<>
          <div style={{
            width: '100%', height: 6, background: 'var(--brand-cream)',
            borderRadius: 3, overflow: 'hidden', marginBottom: 8,
          }}>
            <div style={{
              width: `${Math.max(job.progress, job.phase === 'login' ? 5 : 0)}%`,
              height: '100%',
              background: job.phase === 'uploading' ? 'var(--brand-info)' : 'var(--brand-gold)',
              borderRadius: 3, transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
        </>)}

        {isDone && job.result && (<>
          <div style={{ fontSize: 13, color: 'var(--brand-text)', marginBottom: 8 }}>{typeof job.result.summary === 'string' ? job.result.summary : job.result.claude_summary || 'Erfolgreich abgeschlossen'}</div>
          {job.result.files?.length > 0 && (
            <div>
              {job.result.files.slice(0, 3).map((f, i) => (
                <a key={i} href={`/outputs/${f}?token=${token}`} download style={{
                  display: 'block', fontSize: 12, color: 'var(--brand-gold)', fontWeight: 500, marginTop: 4,
                }}>
                  {f.split('/').pop()}
                </a>
              ))}
              {job.result.files.length > 3 && (
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                  + {job.result.files.length - 3} weitere Dateien
                </div>
              )}
            </div>
          )}
        </>)}

        {isError && (
          <div style={{ fontSize: 13, color: 'var(--brand-danger)' }}>{job.message}</div>
        )}
      </div>
    </div>
  );
}

// ── Globales Fortschrittsfenster (oben rechts, mehrere Jobs gestapelt) ──
function GlobalJobNotification() {
  const { jobs, dismiss } = useJobTracker();
  const [minimizedJobs, setMinimizedJobs] = useState({});

  const jobList = Object.values(jobs);
  if (jobList.length === 0) return null;

  // Laufende zuerst, dann fertige, dann Fehler
  const sorted = [...jobList].sort((a, b) => {
    const order = { running: 0, done: 1, error: 2 };
    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
  });

  const handleMinimize = (jobId, value) => {
    setMinimizedJobs(prev => ({ ...prev, [jobId]: value }));
  };

  return (<>
    {sorted.map((j, i) => (
      <JobNotificationCard
        key={j.jobId}
        job={j}
        index={i}
        minimized={!!minimizedJobs[j.jobId]}
        onDismiss={dismiss}
        onMinimize={handleMinimize}
      />
    ))}
  </>);
}

function ProtectedRoute({ children, minRole, requiredFeature }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  const roles = ['readonly', 'mitarbeiter', 'entwickler', 'manager', 'admin'];
  if (minRole && roles.indexOf(user.role) < roles.indexOf(minRole)) {
    return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--font-body)' }}>Keine Berechtigung.</div>;
  }
  if (requiredFeature && (user.disabled_features || []).includes(requiredFeature)) {
    return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--font-body)' }}>Keine Berechtigung.</div>;
  }
  return children;
}

// Admin-only Gate mit stiller Umleitung: Nicht-Admins werden zur 'target'-Route geschickt.
// Wird für Dashboard + E-Mails benutzt, damit Nicht-Admins bei direktem Aufruf (z.B. Lesezeichen)
// nicht auf einer "Keine Berechtigung"-Seite landen, sondern auf /skills.
function AdminOnlyOrRedirect({ children, target = '/skills' }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== 'admin') return <Navigate to={target} replace />;
  return children;
}

function Topbar() {
  const { user } = useAuth();
  const location = useLocation();
  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : '??';
  const navItems = getNavItems(user);
  const currentItem = navItems.find(item => (
    item.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(item.path)
  ));

  return (
    <header className="topbar">
      <div className="topbar__page-title">{currentItem?.label || 'Dashboard'}</div>
      <div className="topbar__user">
        <span className="topbar__user-name">{user?.name || 'Benutzer'}</span>
        <span className="topbar__avatar">{initials}</span>
      </div>
    </header>
  );
}

function NavItem({ item, mobile = false }) {
  if (item.locked) {
    return (
      <div className="sidebar__item sidebar__item--locked" title="Nur für Admins verfügbar">
        <Icon name={item.icon} size={18} />
        <span>{item.label}</span>
      </div>
    );
  }
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) => `${mobile ? 'mobile-nav__item' : 'sidebar__item'} ${isActive ? `${mobile ? 'mobile-nav__item' : 'sidebar__item'}--active` : ''}`}
    >
      <Icon name={item.icon} size={mobile ? 16 : 18} />
      <span>{item.label}</span>
      {item.earlyDev && <span className="sidebar__badge">Early Dev</span>}
    </NavLink>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  if (!user) return null;

  return (
    <aside className="sidebar">
      <div>
        <SidebarBrand />
        <div className="sidebar__label">Navigation</div>
        {getNavItems(user).map(item => <NavItem key={item.path} item={item} />)}
      </div>
      <div className="sidebar__footer">
        <div className="sidebar__user-name">{user.name}</div>
        <div className="sidebar__user-role">{ROLE_LABELS[user.role] || user.role}</div>
        <button className="sidebar__logout" onClick={logout}>Abmelden</button>
      </div>
    </aside>
  );
}

function MobileNav() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <details className="mobile-nav">
      <summary><Icon name="menu" size={18} />Menü</summary>
      <nav>{getNavItems(user).map(item => <NavItem key={item.path} item={item} mobile />)}</nav>
    </details>
  );
}

// Bestimmte Routen haben ein eigenes Fullscreen-Layout (kein Grid/Sidebar)
const FULLSCREEN_ROUTES = ['/postfach-uebergabe'];

function Layout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user || FULLSCREEN_ROUTES.some(r => location.pathname.startsWith(r))) return children;
  return (
    <div className="app-layout">
      <Topbar />
      <Sidebar />
      <MobileNav />
      <main className="app-layout__main">{children}</main>
      <GlobalJobNotification />
      <FeedbackWidget />
    </div>
  );
}

// ── Auth Callback (Code-Exchange: Token wird nie in URL exponiert) ──
function AuthCallback() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const exchanged = useRef(false);

  useEffect(() => {
    // Verhindere doppelte Aufrufe (React StrictMode)
    if (exchanged.current) return;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const legacyToken = params.get('token');

    if (code) {
      exchanged.current = true;
      fetch(`${API_URL}/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then(r => {
          if (!r.ok) throw new Error(`Exchange failed: ${r.status}`);
          return r.json();
        })
        .then(data => {
          login(data.token);
          window.history.replaceState({}, '', '/');
          navigate('/', { replace: true });
        })
        .catch(err => {
          console.error('Auth exchange error:', err);
          setError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        });
    } else if (legacyToken) {
      exchanged.current = true;
      login(legacyToken);
      window.history.replaceState({}, '', '/');
      navigate('/', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [location.search, login, navigate]);

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--one-dark)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 12, color: '#fff', fontFamily: 'var(--font-body)',
      }}>
        <div style={{ fontSize: 14, color: '#f87171' }}>{error}</div>
      </div>
    );
  }

  return <LoadingScreen />;
}

// ── Loading Screen — One. immo branding ──
function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', background: '#191c1f',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16,
    }}>
      <img src="/assets/logos/one-immo-lockup-on-dark.svg" alt="One. immo" style={{ height: 42, width: 'auto' }} />
      <div style={{ fontSize: 13, color: 'rgba(244,244,244,0.4)', letterSpacing: '1px' }}>
        Wird geladen…
      </div>
    </div>
  );
}

// ── Error Boundary ──
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('React Error Boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#c0392b' }}>Ein Fehler ist aufgetreten</h2>
          <pre style={{ background: '#f8f8f8', padding: 16, borderRadius: 8, fontSize: 13, overflow: 'auto', maxHeight: 300 }}>
            {this.state.error?.toString()}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ marginTop: 16, padding: '10px 20px', background: 'var(--one-dark)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            Seite neu laden
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── App ──
export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <JobProvider>
          <Layout>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/magic-link" element={<AuthMagicLink />} />
              <Route path="/auth/success" element={<AuthCallback />} />
              <Route path="/postfach-uebergabe" element={<PostfachUebergabe />} />
              <Route path="/mieter-formular/:token" element={<TenantForm />} />
              <Route path="/" element={<ProtectedRoute><AdminOnlyOrRedirect target="/skills"><Dashboard /></AdminOnlyOrRedirect></ProtectedRoute>} />
              <Route path="/emails" element={<ProtectedRoute><Emails /></ProtectedRoute>} />
              <Route path="/skills" element={<ProtectedRoute><Skills /></ProtectedRoute>} />
              <Route path="/skills/:skillId" element={<ProtectedRoute minRole="mitarbeiter"><Skills /></ProtectedRoute>} />
              <Route path="/iw24-knowledge" element={<ProtectedRoute><IW24Knowledge /></ProtectedRoute>} />
              <Route path="/briefing" element={<ProtectedRoute><Briefing /></ProtectedRoute>} />
              <Route path="/operator" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/developer-chat" element={<ProtectedRoute requiredFeature="developer-chat-lite"><DeveloperChatLite /></ProtectedRoute>} />
              <Route path="/statistics" element={<ProtectedRoute requiredFeature="statistics"><Statistics /></ProtectedRoute>} />
              <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
              <Route path="/objekt-report" element={<ProtectedRoute><ObjektReport /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute minRole="admin"><Admin /></ProtectedRoute>} />
              {/* Chat + Besprechungen: entfernt 2026-04-13 (Rollout 2026-04-14) */}
            </Routes>
          </Layout>
        </JobProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
